import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useGroupKeys,
  useCreateGroupKey,
  useRevokeGroupKey,
  useDeleteGroupKey,
} from "../../api/groupkeys";
import { ApiError } from "../../api/client";
import { useMyCompany } from "../../api/companies";
import { useGroups } from "../../api/groups";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import type { GroupKey } from "../../api/groupkeys";
import { Plus, Copy, Ban, Trash2 } from "lucide-react";

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } } | undefined;
    return body?.error?.message ?? fallback;
  }
  return fallback;
}

export function GroupKeysPage() {
  const { data, isLoading } = useGroupKeys();
  // A key routes the devices it provisions into one of this company's groups.
  // "This company" is the caller's own, or the company a Riot admin is acting
  // as: /companies/me resolves to the same tenant the key is created for.
  const { data: companyData } = useMyCompany();
  // Loaded only while the create form is open: the picker is its only user.
  const [showCreate, setShowCreate] = useState(false);
  const groupsQuery = useGroups({ counts: false }, { enabled: showCreate });
  const createKey = useCreateGroupKey();
  const revokeKey = useRevokeGroupKey();
  const deleteKey = useDeleteGroupKey();

  const [groupId, setGroupId] = useState("");
  const [description, setDescription] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | {
    action: "revoke" | "delete";
    ids: string[];
    label: string;
  }>(null);

  function showFlash(message: string) {
    setFlash(message);
    setTimeout(() => setFlash(null), 3000);
  }

  const keys = data?.data ?? [];
  const keysById = new Map(keys.map((k) => [k.id, k]));
  const selectedKeys = selectedIds.map((id) => keysById.get(id)).filter(Boolean) as GroupKey[];
  const selectedActive = selectedKeys.filter((k) => k.status === "active");

  async function runConfirmedAction() {
    if (!confirm) return;
    const { action, ids, label } = confirm;
    setConfirm(null);
    try {
      if (action === "revoke") {
        await Promise.all(ids.map((id) => revokeKey.mutateAsync(id)));
        showFlash(`Revoked ${ids.length} ${label}.`);
      } else {
        await Promise.all(ids.map((id) => deleteKey.mutateAsync(id)));
        showFlash(`Deleted ${ids.length} ${label}.`);
      }
      setSelectedIds([]);
    } catch {
      showFlash("Operation failed — see console for details.");
    }
  }

  const columns = [
    {
      key: "groupKey",
      header: "Group Key",
      render: (row: GroupKey) => (
        <div className="flex items-center gap-1.5">
          <code className="text-xs">{row.groupKey.slice(0, 12)}...</code>
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(row.groupKey); showFlash("Copied full group key to clipboard."); }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-gold-400"
            aria-label="Copy key"
          >
            <Copy size={12} />
          </button>
        </div>
      ),
    },
    { key: "groupId", header: "Group ID", render: (row: GroupKey) => <span className="font-mono text-xs">{row.groupId}</span> },
    { key: "groupName", header: "Group Name", render: (row: GroupKey) => row.groupName || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    {
      key: "description",
      header: "Description",
      render: (row: GroupKey) => row.description || <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
    },
    { key: "status", header: "Status", render: (row: GroupKey) => <StatusBadge status={row.status} /> },
    { key: "provisionedCount", header: "Provisioned" },
    { key: "maxDevices", header: "Max", render: (row: GroupKey) => (row.maxDevices === -1 ? "Unlimited" : row.maxDevices) },
    { key: "createdAt", header: "Created", render: (row: GroupKey) => new Date(row.createdAt).toLocaleDateString() },
    {
      key: "actions",
      header: "Actions",
      render: (row: GroupKey) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {row.status === "active" && (
            <button
              onClick={() => setConfirm({ action: "revoke", ids: [row.id], label: "group key" })}
              className="flex items-center gap-1 rounded border border-orange-300 px-2 py-1 text-[11px] font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-950/40"
              title="Revoke — mark this key as revoked (soft)"
            >
              <Ban size={11} /> Revoke
            </button>
          )}
          <button
            onClick={() => setConfirm({ action: "delete", ids: [row.id], label: "group key" })}
            className="flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40"
            title="Delete — permanently remove this key"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      ),
    },
  ];

  const groups = groupsQuery.data?.data ?? [];
  const companyName = companyData?.data.companyName;
  const canCreate = groupId !== "" && !createKey.isPending;

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canCreate) return;
    setCreateError(null);
    createKey.mutate({ groupId, description: description || undefined }, {
      onSuccess: () => { setShowCreate(false); setGroupId(""); setDescription(""); showFlash("Group key created."); },
      onError: (err) => setCreateError(errMessage(err, "Failed to create group key.")),
    });
  }

  return (
    <div>
      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {flash}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[min(420px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {confirm.action === "revoke" ? "Revoke group key" : "Permanently delete group key"}
              {confirm.ids.length > 1 ? "s" : ""}
            </h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">
              {confirm.action === "revoke"
                ? `Revoking blocks future provisioning but keeps the audit trail. Already-provisioned devices are not affected. This applies to ${confirm.ids.length} key${confirm.ids.length === 1 ? "" : "s"}.`
                : `Deleting removes the key row completely and cannot be undone. This applies to ${confirm.ids.length} key${confirm.ids.length === 1 ? "" : "s"}.`}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={runConfirmedAction}
                className={
                  confirm.action === "revoke"
                    ? "rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
                    : "rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                }
              >
                {confirm.action === "revoke" ? "Revoke" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-4">
          <h3 className="mb-1 text-sm font-medium text-gray-900 dark:text-white">Create Group Key</h3>
          <p className="mb-3 text-xs text-gray-600 dark:text-riot-navy-200">
            {`Keys created here route devices into ${companyName ?? "your company"}. A device provisioned with the key joins the group you choose.`}
          </p>
          {groupsQuery.isError && (
            <p id="group-key-groups-error" role="alert" className="mb-3 text-xs text-red-600 dark:text-red-400">
              Could not load your groups. Try again later.
            </p>
          )}
          {groupsQuery.isSuccess && groups.length === 0 && (
            <p className="mb-3 text-xs text-gray-600 dark:text-riot-navy-200">
              Your company has no groups yet.{" "}
              <Link to="/groups" className="font-medium text-riot-navy-700 underline dark:text-riot-gold-400">
                Create a group
              </Link>{" "}
              first, then come back to make a key for it.
            </p>
          )}
          <label htmlFor="group-key-group" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
            Group
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <select
              id="group-key-group"
              value={groupId}
              onChange={(e) => { setGroupId(e.target.value); setCreateError(null); }}
              required
              aria-describedby={
                [groupsQuery.isError && "group-key-groups-error", createError && "group-key-create-error"]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              disabled={groupsQuery.isLoading || groups.length === 0}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
            >
              <option value="">{groupsQuery.isLoading ? "Loading groups…" : "Select a group…"}</option>
              {groups.map((g) => (
                <option key={g.groupId} value={g.groupId}>
                  {g.name}
                </option>
              ))}
            </select>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
            <button type="submit" disabled={!canCreate} className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50">
              {createKey.isPending ? "Creating..." : "Create"}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setCreateError(null); }} className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-riot-navy-700">Cancel</button>
          </div>
          {createError && (
            <p id="group-key-create-error" role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
              {createError}
            </p>
          )}
        </form>
      )}

      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading group keys...</div>
      ) : (
        <DataTable
          title="Group Keys"
          totalCount={data?.data.length}
          columns={columns}
          data={keys}
          keyExtractor={(k) => k.id}
          selectable
          onSelectionChange={setSelectedIds}
          filters={{
            searchPlaceholder: "Search keys",
            searchFields: [
              { value: "groupName", label: "Group name" },
              { value: "groupId", label: "Group ID" },
              { value: "description", label: "Description" },
            ],
            statusOptions: [
              { value: "active", label: "Active" },
              { value: "revoked", label: "Revoked" },
            ],
          }}
          actions={
            <>
              {selectedActive.length > 0 && (
                <button
                  onClick={() =>
                    setConfirm({
                      action: "revoke",
                      ids: selectedActive.map((k) => k.id),
                      label: `active group key${selectedActive.length === 1 ? "" : "s"}`,
                    })
                  }
                  className="flex items-center gap-1.5 rounded-md border border-orange-300 px-3.5 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-800/60 dark:text-orange-300 dark:hover:bg-orange-950/40"
                >
                  <Ban size={12} /> Revoke ({selectedActive.length})
                </button>
              )}
              {selectedIds.length > 0 && (
                <button
                  onClick={() =>
                    setConfirm({
                      action: "delete",
                      ids: selectedIds,
                      label: `group key${selectedIds.length === 1 ? "" : "s"}`,
                    })
                  }
                  className="flex items-center gap-1.5 rounded-md border border-red-300 px-3.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  <Trash2 size={12} /> Delete ({selectedIds.length})
                </button>
              )}
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
              >
                <Plus size={14} /> Create Key
              </button>
            </>
          }
        />
      )}
    </div>
  );
}
